// ./APIs/Authentication.js
// This is just used to check if a token key set in LocalStorage for now.   When the authentication microservice is better built out we will check for more.

import history from "./History";
import axios from "axios";
import * as moment from "moment";
import { promised } from "q";
import config from "../config.json";

const Authentication = {
  
  login(email, password) {
    return new Promise(function (resolve, reject) {
      // id2.shippersedge.com/carrier/login is on the identities repository / service
      axios
        .post("https://id2.shippersedge.com/carrier/login", {}, {
          auth: {
            username: email,
            password: password
          }
        })
        .then(response => {
          // console.log("Successful Login! (Authentication/loginGo)", response);
          const results = response.data.results || [];

          // crossPortalNotice (see identity's own applyVersionFilter) -- present
          // whenever this login also has clients/carriers on cpv3. Stored
          // regardless of whether results is empty or not; getCrossPortalNotice()
          // below is the accessor. crossPortalNoticeDismissed is reset on every
          // fresh login (not just cleared once) so the notice is shown again
          // next time someone logs in, even if a previous login dismissed it.
          localStorage.setItem("crossPortalNotice", JSON.stringify(response.data.crossPortalNotice || null));
          localStorage.removeItem("crossPortalNoticeDismissed");

          if (results.length === 0) {
            // Every one of this login's carrier associations is on cpv3 --
            // nothing to select here at all (see identity's applyVersionFilter,
            // which already filtered them out of `results`). This used to reach
            // `results[0].id` below unconditionally, which threw immediately
            // (results[0] is undefined) -- inside this .then(), NOT caught by
            // the .catch() below (that only catches the axios request error,
            // not an error thrown in the success handler), so resolve() never
            // ran and Login.js just sat there with an unhandled rejection and
            // zero user-facing feedback. Still a genuinely successful login
            // (credentials were correct) -- resolve normally so Login.js can
            // route to the portal chooser instead of /Dashboard, same as
            // carrier-portal-frontend-3's NoPortalAccessPage.
            localStorage.setItem("multiCarrierWelcomeShowHide", "hide");
            localStorage.setItem("token", "cross-portal-only");
            localStorage.setItem("emailAddress", email);
            localStorage.setItem("userData", "{}");
            localStorage.setItem("userData2", "[]");
            localStorage.setItem("multiCarrierSupport", "yes");
            localStorage.setItem("loginDateTime", moment().format("YYYY-MM-DD"));
            resolve(response);
            return;
          }

          if (results.length > 1) {
            localStorage.setItem("multiCarrierWelcomeShowHide", "show");
          } else {
            localStorage.setItem("multiCarrierWelcomeShowHide", "hide");
          }
          let currentUser = results[0];
          let allUserData = results;
          // console.log(currentUser)
          localStorage.setItem("id", currentUser.id);
          localStorage.setItem("carrierId", currentUser.carrierId);
          localStorage.setItem("name", currentUser.name);
          localStorage.setItem("carrierName", currentUser.carrierName);
          localStorage.setItem("phone", currentUser.phone);
          localStorage.setItem("emailAddress", currentUser.email);
          // localStorage.setItem("secret", response.data.token);
          localStorage.setItem("token", currentUser.id);
          localStorage.setItem("clientName", currentUser.clientName);
          localStorage.setItem("carrierClientId", currentUser.clientCode);
          localStorage.setItem("clientLogo", currentUser.clientLogo);
          localStorage.setItem("userData", JSON.stringify(currentUser));
          localStorage.setItem("userData2", JSON.stringify(allUserData));
          localStorage.setItem("multiCarrierSupport", "yes");
          resolve(response);
          localStorage.setItem("loginDateTime", moment().format("YYYY-MM-DD"));
        })
        .catch(error => {
          if (error) {
            // console.log("Login error, getLoginGo2, status:", error.response.status);
            reject(false);
          }
        });
      return promised;
    });
  },

  getUserData() {
    if (JSON.parse(localStorage.getItem("userData2"))) {
      return JSON.parse(localStorage.getItem("userData2"))
    } else {
      console.warn('Authentication.getUserData failed')
      return false
    }
  },

  // { otherPortalClients, otherPortalUrl, explanation: {en,es,fr,pt,no} } or
  // null -- see identity's own applyVersionFilter. Never throws on bad JSON;
  // treated the same as "no notice" either way.
  getCrossPortalNotice() {
    try {
      return JSON.parse(localStorage.getItem("crossPortalNotice")) || null;
    } catch {
      return null;
    }
  },

  // False only for the "every carrier association is on cpv3" login case
  // (see login()'s own comment) -- Dashboard.js assumes at least one
  // userData2 entry unconditionally (loadContextUserData/setActiveCollections
  // both do `selected[0].something` with no guard), so this is checked before
  // ever routing there.
  hasLocalCarrierAccess() {
    const userData2 = JSON.parse(localStorage.getItem("userData2") || "[]");
    return Array.isArray(userData2) && userData2.length > 0;
  },

  isLoggedIn() {
    let lcToken = localStorage.getItem("token");
    if (lcToken) {
      if (
        history.location.pathname === "/Login" ||
        history.location.pathname === "/"
      ) {
        if (!this.hasLocalCarrierAccess()) {
          history.push("/PortalChooser");
          return;
        }
        const lastRoute = localStorage.getItem("lastRoute");
        const validRoutes = ["/Dashboard", "/Accounting", "/History"];
        let targetRoute = "/Dashboard";
        if (lastRoute && validRoutes.includes(lastRoute) && lastRoute !== "/Dashboard") {
            const clientCode = localStorage.getItem("carrierClientId");
            const carrierId = localStorage.getItem("carrierId");
            const routeName = lastRoute.replace("/", "");
            const entry = config.dropDownNavigation.find(e => e.clientCode === clientCode);
            const hasAccess = entry &&
                (entry.carrierIds === "*" || (Array.isArray(entry.carrierIds) && entry.carrierIds.includes(carrierId))) &&
                entry.routes.includes(routeName);
            if (hasAccess) targetRoute = lastRoute;
        }
        history.push(targetRoute);
      }
    } else {
      console.warn("token NOT present, redirect to login");
      history.push("/Login");
    }
    let lastLoginDate = localStorage.getItem("loginDateTime");
    let diffDays = moment().diff(lastLoginDate, "days");
    let multiCarrierSupport =  localStorage.getItem("multiCarrierSupport")
    //diffDays will turn into NaN if it is corrupted or otherwise wonky so need to test for NaN conditons.
    if ((diffDays > 7 || lastLoginDate === null || isNaN(diffDays)) || multiCarrierSupport === null) {
      console.log("Login Expired.");
      localStorage.setItem(
        "forceLogoutReason",
        "Your login session has expired, please login again."
      );
      localStorage.clear();
      history.push("/Login");
    }
  },

  profileUpdate(profileUpdate) {
    return new Promise(function (resolve, reject) {
      // console.log("Authentication.js, profileUpdate,", profileUpdate.values());
      axios
        .post(
          "https://schuff.shippersedge.com/public/carrier/updateProfile.cfm?wsdl",
          profileUpdate
        )
        .then(response => {
          // console.log("wat", profileUpdate.get("email"));
          localStorage.setItem("name", profileUpdate.get("name"));
          localStorage.setItem("email", profileUpdate.get("email"));
          localStorage.setItem("phone", profileUpdate.get("phone"));
          resolve(response);
        })
        .catch(error => {
          if (error) {
            reject("Authenticat.js, profileUpdate, error", error);
          }
        });
      return promised;
    });
  },

  getID() {
    return localStorage.getItem("id");
  },

  getEmailAddress() {
    return localStorage.getItem("emailAddress");
  },

  getName() {
    return localStorage.getItem("name");
  },

  getPhone() {
    return localStorage.getItem("phone");
  },

  logOut() {
    const lastRoute = localStorage.getItem("lastRoute");
    localStorage.clear();
    if (lastRoute) localStorage.setItem("lastRoute", lastRoute);
    history.push("/Login");
  }
};

export default Authentication;
